// ═══════════════════════════════════════════════════════════════════════════
// Master ERP navigation tree — SINGLE SOURCE OF TRUTH for the global search.
// ═══════════════════════════════════════════════════════════════════════════
//
// STRICT RULE: every entry below must correspond to a navigation item the user
// can actually see in the running UI — a sidebar row, a top-of-page tab, or
// an inner sub-tab. Labels must match what is rendered on screen exactly.
//
// Sources of truth (in order of priority):
//   • Sidebar entries → `sms/src/app/shared/constants/nav-menu.constants.ts`
//     (uncommented entries in adminNavList / hrmsNavList / feeNavList / etc.)
//   • Shell-page tab strips → each module shell's component HTML
//     (e.g. hrms-attendance.component.html, hrms-leave.component.html)
//   • Inner sub-tabs → the page component's HTML
//
// To add / hide / rename a page in the search bar:
//   • ADD a new entry to the right module below — but only if it's visible
//     in the actual UI with that exact label
//   • HIDE a page → comment its line out (// { ... }) — search will skip it
//   • RENAME → update both the rendered label in the UI AND the label here
//
// `roles` controls who sees the entry; empty/absent = visible to all.
// ═══════════════════════════════════════════════════════════════════════════

export interface NavNode {
  label: string;
  /** Required for everything except 'group'. */
  path?: string;
  roles?: string[];
  /** 'page' (default) — sidebar entry; 'tab' — top-of-page primary tab;
   *  'subtab' — secondary tab inside a page; 'group' — a logical section
   *  used ONLY for breadcrumb grouping (not searchable itself, not clickable).
   *  Use 'group' only when the sidebar shows the parent label as a non-clickable
   *  header that ONLY exists to organize children. If the sidebar parent has
   *  a real linkto and is clickable, model it as a 'page'. */
  kind?: 'page' | 'tab' | 'subtab' | 'group';
  external?: boolean;
  /** Page (and all descendants) needs the user to be a teacher — either by
   *  role or via teacher impersonation — to load real data. Search still
   *  shows these entries, but greys them out for non-teachers. */
  requiresImpersonation?: boolean;
  children?: NavNode[];
}

const R_ADMIN = ['super_admin', 'system_admin', 'it_head'];
const R_HRMS = ['super_admin', 'hrms_admin', 'hrms_user'];
const R_HRMS_ADMIN = ['super_admin', 'hrms_admin'];
const R_FINANCE = ['super_admin', 'finance_account_admin'];
const R_TEACHER = ['super_admin', 'teacher_acadamic_user', 'teacher_academic_admin', 'Teacher'];
const R_TRANSPORT_ADMIN = ['super_admin', 'transport_admin'];
const R_TRANSPORT_CARETAKER = ['transport_caretaker'];
const R_TRANSPORT_DRIVER = ['transport_driver'];
const R_LIBRARY_ADMIN = ['super_admin', 'library_admin'];
const R_LIBRARY_USER = ['library_user'];
const R_ASSET = ['super_admin', 'asset_admin'];
const R_STORE = ['super_admin'];
const R_MIS = ['super_admin', 'mis_admin'];
const R_APPTRACKER = ['super_admin'];

export const NAV_TREE: NavNode[] = [
  // ── Home (visible to everyone with a session) ──────────────────────────
  { label: 'Dashboard', path: '/nora/dashboard' },

  // ── Admin ──────────────────────────────────────────────────────────────
  // Mirrors adminNavList in nav-menu.constants.ts.
  {
    label: 'Admin', path: '/nora/admin/dashboard', roles: R_ADMIN,
    children: [
      { label: 'Dashboard', path: '/nora/admin/dashboard' },
      {
        label: 'Student Management', path: '/nora/admin/student-management',
        children: [
          { label: 'Students', path: '/nora/admin/student-management/student' },
          { label: 'Inactive Students', path: '/nora/admin/student-management/inactive-students' },
          { label: 'Parents', path: '/nora/admin/student-management/parent' },
          { label: 'Student Analytics', path: '/nora/admin/student-management/academic-year' },
          { label: 'Attendance', path: '/nora/admin/student-management/attendance-history' },
          {
            label: 'Certificate', path: '/nora/admin/student-management/certificate',
            children: [
              { label: 'TC', path: '/nora/admin/student-management/certificate?tab=tc', kind: 'tab' },
              { label: 'Conduct Certificate', path: '/nora/admin/student-management/certificate?tab=conduct', kind: 'tab' },
            ],
          },
          { label: 'Holistic Report', path: '/nora/admin/student-management/holistic-report' },
          {
            // Wired via smartcard-config.component.ts queryParamMap subscription.
            label: 'Smartcard Configuration', path: '/nora/admin/student-management/smartcard-configuration',
            children: [
              { label: 'Templates', path: '/nora/admin/student-management/smartcard-configuration?tab=templates', kind: 'tab' },
              { label: 'Assign & Print', path: '/nora/admin/student-management/smartcard-configuration?tab=assign', kind: 'tab' },
            ],
          },
        ],
      },
      {
        label: 'Staff Management', path: '/nora/admin/staff-management',
        children: [
          { label: 'Teacher Management', path: '/nora/admin/staff-management/teacher-management' },
          { label: 'Non-Teaching Staff List', path: '/nora/admin/staff-management/non-teaching-staff-list' },
        ],
      },
      { label: 'Class Management', path: '/nora/admin/class-management' },
      // Timetable is an external link in the sidebar — intentionally omitted
      // from search so we don't navigate to an external host on click.
      {
        label: 'Examination', path: '/nora/admin/exam-timetable',
        children: [
          { label: 'Exam Configuration', path: '/nora/admin/exam-timetable', kind: 'tab' },
          { label: 'Exam Result', path: '/nora/admin/exam-timetable/result', kind: 'tab' },
        ],
      },
      { label: 'Messages', path: '/nora/admin//message' }, // sidebar route has a double slash
      {
        label: 'Communication', path: '/nora/admin/live-meeting',
        children: [
          { label: 'Meeting Links', path: '/nora/admin/live-meeting', kind: 'tab' },
          { label: 'Notice Board', path: '/nora/admin/live-meeting/notice-board', kind: 'tab' },
          { label: 'Events', path: '/nora/admin/live-meeting/events', kind: 'tab' },
        ],
      },
      { label: 'Master Data', path: '/nora/admin/master' },
      { label: 'Reports', path: '/nora/admin/reports' },
    ],
  },

  // ── HRMS ───────────────────────────────────────────────────────────────
  // Mirrors hrmsNavList in nav-menu.constants.ts.
  {
    label: 'HRMS', path: '/nora/hrms/profile/personal', roles: R_HRMS,
    children: [
      {
        label: 'My Profile', path: '/nora/hrms/profile',
        // Tabs from tab-buttons.component.ts:20-31. Filemanager is commented;
        // Experience Certificates is admin-only (filteredTabs in same file).
        children: [
          { label: 'Personal', path: '/nora/hrms/profile/personal', kind: 'tab' },
          {
            // Wired via team.component.ts queryParamMap subscription.
            label: 'Team', path: '/nora/hrms/profile/team', kind: 'tab',
            children: [
              { label: 'Reporting Managers', path: '/nora/hrms/profile/team?tab=manager', kind: 'subtab' },
              { label: 'Direct Reports', path: '/nora/hrms/profile/team?tab=direct', kind: 'subtab' },
            ],
          },
          { label: 'Education', path: '/nora/hrms/profile/education', kind: 'tab' },
          {
            // Wired via family.component.ts queryParamMap subscription.
            label: 'Family', path: '/nora/hrms/profile/family', kind: 'tab',
            children: [
              { label: 'Family Members', path: '/nora/hrms/profile/family?tab=family', kind: 'subtab' },
              { label: 'Emergency Contacts', path: '/nora/hrms/profile/family?tab=emergency', kind: 'subtab' },
            ],
          },
          {
            // Documents tab; inner sub-tabs from document.component.html.
            label: 'Documents', path: '/nora/hrms/profile/document', kind: 'tab',
            children: [
              { label: "ID's Proof", path: '/nora/hrms/profile/document/document', kind: 'subtab' },
              { label: 'Certification', path: '/nora/hrms/profile/document/certification', kind: 'subtab' },
              { label: 'Work', path: '/nora/hrms/profile/document/work', kind: 'subtab' },
            ],
          },
          { label: 'Work Week', path: '/nora/hrms/profile/work-week', kind: 'tab' },
          {
            // Payroll tab in My Profile; inner sub-tabs from payroll.component.html.
            label: 'Payroll', path: '/nora/hrms/profile/pay-slip', kind: 'tab',
            children: [
              { label: 'Pay Slip', path: '/nora/hrms/profile/pay-slip/pay-slip', kind: 'subtab' },
              { label: 'Salary Structure', path: '/nora/hrms/profile/pay-slip/salary-structure', kind: 'subtab' },
              { label: 'Bank Account', path: '/nora/hrms/profile/pay-slip/bank-account', kind: 'subtab' },
            ],
          },
          {
            // E-Exit tab; inner sub-tabs from e-exit.component.html.
            label: 'E-Exit', path: '/nora/hrms/profile/e-exit', kind: 'tab',
            children: [
              { label: 'E-Exit', path: '/nora/hrms/profile/e-exit/e-exit', kind: 'subtab' },
              { label: 'Resignation Applied', path: '/nora/hrms/profile/e-exit/resignation-applied', kind: 'subtab', roles: R_HRMS_ADMIN },
            ],
          },
          { label: 'Experience Certificates', path: '/nora/hrms/profile/experience-certificate', kind: 'tab', roles: R_HRMS_ADMIN },
        ],
      },
      {
        label: 'Directory', path: '/nora/hrms/directory',
        // Tabs from directory.component.html.
        children: [
          { label: 'Directory', path: '/nora/hrms/directory/directory', kind: 'tab' },
          { label: 'Inactive Employees', path: '/nora/hrms/directory/inactive', kind: 'tab', roles: R_HRMS_ADMIN },
          { label: 'Verify', path: '/nora/hrms/directory/verify', kind: 'tab', roles: R_HRMS_ADMIN },
          { label: 'Smartcard Configuration', path: '/nora/hrms/directory/hrms-smartcard', kind: 'tab', roles: R_HRMS_ADMIN },
        ],
      },
      {
        label: 'Leave', path: '/nora/hrms/leave',
        // Tabs from hrms-leave.component.html:4-23. Visibility varies by role.
        children: [
          { label: 'Apply Leave', path: '/nora/hrms/leave/leave', kind: 'tab' },
          { label: 'My Logs', path: '/nora/hrms/leave/my-logs', kind: 'tab' },
          {
            // Wired via team-logs.component.ts queryParamMap subscription.
            label: 'Team Logs', path: '/nora/hrms/leave/team-logs', kind: 'tab', roles: R_HRMS_ADMIN,
            children: [
              { label: 'Leave Requests', path: '/nora/hrms/leave/team-logs?tab=requests', kind: 'subtab' },
              { label: 'Leave History', path: '/nora/hrms/leave/team-logs?tab=history', kind: 'subtab' },
            ],
          },
          { label: 'Balance', path: '/nora/hrms/leave/balance', kind: 'tab', roles: R_HRMS_ADMIN },
          { label: 'Rules', path: '/nora/hrms/leave/rules', kind: 'tab', roles: R_HRMS_ADMIN },
          { label: 'Assign Rules', path: '/nora/hrms/leave/assign-rules', kind: 'tab', roles: R_HRMS_ADMIN },
        ],
      },
      {
        label: 'Payroll', path: '/nora/hrms/payroll', roles: R_HRMS_ADMIN,
        children: [
          {
            // Setup Payroll page tabs from setup-payroll.component.html.
            label: 'Setup Payroll', path: '/nora/hrms/payroll/setup-payroll',
            children: [
              { label: 'Salary Component', path: '/nora/hrms/payroll/setup-payroll/salary-component', kind: 'tab' },
              { label: 'Create Salary Structure', path: '/nora/hrms/payroll/setup-payroll/create-salary-structure', kind: 'tab' },
              { label: 'Assign Salary Structure', path: '/nora/hrms/payroll/setup-payroll/assign-salary-structure', kind: 'tab' },
            ],
          },
          {
            // Run Payroll page tabs from run-payroll.component.html.
            label: 'Run Payroll', path: '/nora/hrms/payroll/run-payroll',
            children: [
              { label: 'Attendance', path: '/nora/hrms/payroll/run-payroll/attendance', kind: 'tab' },
              { label: 'Salary Revision', path: '/nora/hrms/payroll/run-payroll/salary-revision', kind: 'tab' },
              { label: 'Pay Register', path: '/nora/hrms/payroll/run-payroll/pay-register', kind: 'tab' },
              {
                // "Variable & Adhoc" tab; inner sub-tabs from variable-adhoc.component.html.
                label: 'Variable & Adhoc', path: '/nora/hrms/payroll/run-payroll/adhoc', kind: 'tab',
                children: [
                  { label: 'Adhoc', path: '/nora/hrms/payroll/run-payroll/adhoc/adhoc', kind: 'subtab' },
                  { label: 'Adhoc Earnings', path: '/nora/hrms/payroll/run-payroll/adhoc/adhoc-earnings', kind: 'subtab' },
                  { label: 'Adhoc Deduction', path: '/nora/hrms/payroll/run-payroll/adhoc/adhoc-deduction', kind: 'subtab' },
                ],
              },
              { label: 'Salary On Hold', path: '/nora/hrms/payroll/run-payroll/salary-on-hold', kind: 'tab' },
            ],
          },
        ],
      },
      {
        label: 'Attendance', path: '/nora/hrms/attendance',
        // Tabs from hrms-attendance.component.html:4-28.
        children: [
          // My Logs is now a single page (Today section + Monthly Log table).
          // Old Daily/Monthly inner sub-tabs were merged in DFCT0013687.
          { label: 'My Logs', path: '/nora/hrms/attendance/attendance', kind: 'tab' },
          {
            // Admin-only shell tab; its inner sub-tabs come from
            // attendance-monthly-log-main.component.html:5-12.
            label: 'Attendance Summary', path: '/nora/hrms/attendance/attendance-month-logs-admin', kind: 'tab', roles: R_HRMS_ADMIN,
            children: [
              { label: 'Monthly Log', path: '/nora/hrms/attendance/attendance-month-logs-admin/month-log', kind: 'subtab' },
              { label: 'Comp-Off Logs', path: '/nora/hrms/attendance/attendance-month-logs-admin/comp-off', kind: 'subtab' },
            ],
          },
          // Non-admin sees this instead of "Attendance Summary"; no inner tabs rendered.
          { label: 'Comp-Off Logs', path: '/nora/hrms/attendance/attendance-month-logs-user', kind: 'tab' },
          { label: 'Penalty Logs', path: '/nora/hrms/attendance/automation-logs', kind: 'tab' },
          {
            label: 'Approvals', path: '/nora/hrms/attendance/approvals', kind: 'tab', roles: R_HRMS_ADMIN,
            children: [
              { label: 'Requests', path: '/nora/hrms/attendance/approvals/approvals', kind: 'subtab' },
              { label: 'Accepted', path: '/nora/hrms/attendance/approvals/accepted', kind: 'subtab' },
            ],
          },
          { label: 'Attendance Rules', path: '/nora/hrms/attendance/rules', kind: 'tab' },
          { label: 'Assign Attendance Rule', path: '/nora/hrms/attendance/assign-rules', kind: 'tab', roles: R_HRMS_ADMIN },
          { label: 'Work Week Rules', path: '/nora/hrms/attendance/work-week', kind: 'tab', roles: R_HRMS_ADMIN },
          { label: 'Assign Work Week Rule', path: '/nora/hrms/attendance/assign-work-week', kind: 'tab', roles: R_HRMS_ADMIN },
        ],
      },
      {
        // Tabs from hrms-holiday-calendar.component.html.
        label: 'Holiday Calendar', path: '/nora/hrms/calendar',
        children: [
          { label: 'Calendar', path: '/nora/hrms/calendar/calendar', kind: 'tab' },
          { label: 'Holiday List', path: '/nora/hrms/calendar/holiday-list', kind: 'tab' },
        ],
      },
      { label: 'Reports', path: '/nora/hrms/reports', roles: R_HRMS_ADMIN },
    ],
  },

  // ── Fee Management ─────────────────────────────────────────────────────
  // Mirrors feeNavList in nav-menu.constants.ts.
  {
    label: 'Fee', path: '/nora/fee-management/dashboard', roles: R_FINANCE,
    children: [
      { label: 'Dashboard', path: '/nora/fee-management/dashboard' },
      { label: 'Fee Actions Needed', path: '/nora/fee-management/fee-actions-needed' },
      {
        // Grouped under "Collection" to match the sidebar (feeNavList).
        label: 'Collection', path: '/nora/fee-management/payments-collections/payment',
        children: [
          {
            // The page renders a tab strip (payment-history / arrear-history-view).
            label: 'Student Payment Tracker', path: '/nora/fee-management/payments-collections/payment',
            children: [
              { label: 'Student Payment Tracker', path: '/nora/fee-management/payments-collections/payment', kind: 'tab' },
              { label: 'Previous Year Arrears', path: '/nora/fee-management/payments-collections/previous-year-arrears', kind: 'tab' },
            ],
          },
          { label: 'Direct Collection & Refund', path: '/nora/fee-management/direct-fee-collection' },
          // Superseded: approvals now live in the Optional Fee Enrolment page's "Pending Workflows" tab.
          // { label: 'Optional Fee Register', path: '/nora/fee-management/fee-requests' },
          { label: 'Optional Fee Enrolment', path: '/nora/fee-management/optional-fee-enrolment' },
        ],
      },
      {
        label: 'Fee Management', path: '/nora/fee-management/configuration-fee-structure',
        children: [
          { label: 'Create Fee Rule', path: '/nora/fee-management/configuration-fee-structure' },
          { label: 'Fee Adjustments', path: '/nora/fee-management/fee-adjustment-rule' },
          { label: 'Fines', path: '/nora/fee-management/fee-fine-management' },
        ],
      },
      { label: 'Manual Transportation Fee Assignment', path: '/nora/fee-management/manual-transportation-fee-assignment' },
      {
        label: 'Expenses', path: '/nora/fee-management/expense',
        children: [
          { label: 'Add Expenses', path: '/nora/fee-management/expense' },
          { label: 'Setup Expense Category', path: '/nora/fee-management/configuration-expense-category' },
        ],
      },
      { label: 'Reports', path: '/nora/fee-management/reports' },
      {
        label: 'Configuration & Settings', path: '/nora/fee-management/configuration-terms',
        children: [
          { label: 'Fee Terms', path: '/nora/fee-management/configuration-term' },
          { label: 'Fee Category', path: '/nora/fee-management/configuration-fee-category' },
        ],
      },
    ],
  },

  // ── Teacher (regular) ──────────────────────────────────────────────────
  // Mirrors teacherNavList in nav-menu.constants.ts.
  {
    label: 'Teacher', path: '/nora/teacher/dashboard', roles: R_TEACHER,
    requiresImpersonation: true,
    children: [
      { label: 'Dashboard', path: '/nora/teacher/dashboard' },
      { label: 'Messages', path: '/nora/teacher/message' },
      { label: 'Timetable', path: '/nora/teacher/timetable' },
      { label: 'My Classes', path: '/nora/teacher/students' },
      { label: 'Holistic Report', path: '/nora/teacher/progress-card/students-marking' },
      { label: 'Homework', path: '/nora/teacher/homework' },
      { label: 'Student Fee Assignment', path: '/nora/teacher/student-fee-assignment' },
      { label: 'Attendance', path: '/nora/teacher/attendance' },
      { label: 'Marks & Grades', path: '/nora/teacher/gradebook' },
      { label: 'Promotion', path: '/nora/teacher/promotions' },
      { label: 'Achievements', path: '/nora/teacher/students-achivements' },
      { label: 'Communication', path: '/nora/teacher/live-meeting' },
      { label: 'Weekly Planner', path: '/nora/teacher/weekly-planner' },
      { label: 'Reports', path: '/nora/teacher/reports' },
    ],
  },

  // ── Transport (admin / super_admin) ────────────────────────────────────
  // Mirrors transportNavList in nav-menu.constants.ts (grouped layout).
  {
    label: 'Transport', path: '/nora/transport/dashboard', roles: R_TRANSPORT_ADMIN,
    children: [
      { label: 'Dashboard', path: '/nora/transport/dashboard' },
      { label: 'Live Tracking', path: '/nora/transport/live-trackinghome' },
      {
        label: 'Operations', path: '/nora/transport/trip-history',
        children: [
          { label: 'Trip History', path: '/nora/transport/trip-history' },
          { label: 'Attendance Tracker', path: '/nora/transport/attendance-tracking' },
          { label: 'Boarding History', path: '/nora/transport/drop-history' },
        ],
      },
      {
        label: 'Routes & Fleet', path: '/nora/transport/bus-routes',
        children: [
          { label: 'Routes & Stops', path: '/nora/transport/bus-routes' },
          { label: 'Boarding Points', path: '/nora/transport/pickup-points' },
          { label: 'Vehicle Management', path: '/nora/transport/fleet-management' },
          { label: 'Driver Directory', path: '/nora/transport/driver-management' },
          { label: 'Caretaker Directory', path: '/nora/transport/caretaker-management' },
        ],
      },
      {
        label: 'Students', path: '/nora/transport/student-directory',
        children: [
          { label: 'Student Directory', path: '/nora/transport/student-directory' },
          {
            // Wired via fee-management.component.ts queryParamMap subscription.
            label: 'Fee Management', path: '/nora/transport/fee-management',
            children: [
              { label: 'Fee Catalog', path: '/nora/transport/fee-management?tab=fee-catalog', kind: 'tab' },
              { label: 'Fee Adjustments', path: '/nora/transport/fee-management?tab=adjustment', kind: 'tab' },
              { label: 'Students List', path: '/nora/transport/fee-management?tab=student-list', kind: 'tab' },
            ],
          },
        ],
      },
      {
        label: 'Maintenance', path: '/nora/transport/maintenance',
        children: [
          { label: 'Vehicle Service Data', path: '/nora/transport/maintenance' },
          { label: 'Assign Device', path: '/nora/transport/authorization' },
        ],
      },
      { label: 'Reports', path: '/nora/transport/reports' },
    ],
  },

  // ── Transport (caretaker) ──────────────────────────────────────────────
  // Mirrors transportNavListForCareTaker. Caretaker sees a reduced sidebar.
  {
    label: 'Transport', path: '/nora/transport/care-taker/pickup-points', roles: R_TRANSPORT_CARETAKER,
    children: [
      { label: 'Boarding Points', path: '/nora/transport/care-taker/pickup-points' },
      { label: 'Driver Directory', path: '/nora/transport/care-taker/driver-management' },
      { label: 'Caretaker Directory', path: '/nora/transport/care-taker/caretaker-management' },
      { label: 'Student Directory', path: '/nora/transport/care-taker/student-directory' },
      { label: 'Vehicle Service Data', path: '/nora/transport/maintenance' },
    ],
  },

  // ── Transport (driver) ─────────────────────────────────────────────────
  // Mirrors transportNavListForDriver. Driver sees a reduced sidebar.
  {
    label: 'Transport', path: '/nora/transport/driver/pickup-points', roles: R_TRANSPORT_DRIVER,
    children: [
      { label: 'Boarding Points', path: '/nora/transport/driver/pickup-points' },
      { label: 'Driver Directory', path: '/nora/transport/driver/driver-management' },
      { label: 'Caretaker Directory', path: '/nora/transport/driver/caretaker-management' },
      { label: 'Student Directory', path: '/nora/transport/driver/student-directory' },
      { label: 'Vehicle Service Data', path: '/nora/transport/maintenance' },
    ],
  },

  // ── Library (admin) ────────────────────────────────────────────────────
  // Mirrors libraryNavList in nav-menu.constants.ts (admin-role entries).
  {
    label: 'Library', path: '/nora/library/e-books', roles: R_LIBRARY_ADMIN,
    children: [
      {
        label: 'Digital Library', path: '/nora/library/e-books',
        children: [
          { label: 'Dashboard', path: '/nora/library/e-books/dashboard' },
          { label: 'Add New Content', path: '/nora/library/e-books/ebook' },
          { label: 'Content Request', path: '/nora/library/e-books/request' },
          { label: 'Media Library', path: '/nora/library/e-books/content' },
        ],
      },
      {
        label: 'Physical Books', path: '/nora/library/physical-books',
        children: [
          { label: 'Dashboard', path: '/nora/library/physical-books/dashboard' },
          { label: 'Book Management', path: '/nora/library/physical-books/book-management' },
          { label: 'Inventory Management', path: '/nora/library/physical-books/inventory-management' },
          { label: 'Reservation Queue', path: '/nora/library/physical-books/reservation-queue' },
          { label: 'Fine Management', path: '/nora/library/physical-books/fine-management' },
          { label: 'Reports', path: '/nora/library/physical-books/reports' },
          { label: 'Configuration', path: '/nora/library/physical-books/configuration' },
        ],
      },
      {
        // Tabs from master-data.component.html.
        label: 'Master Data', path: '/nora/library/master-data',
        children: [
          { label: 'Master Data Overview', path: '/nora/library/master-data', kind: 'tab' },
          { label: 'Categories', path: '/nora/library/master-data/category', kind: 'tab' },
          { label: 'Sub Categories', path: '/nora/library/master-data/subcategory', kind: 'tab' },
          { label: 'Genre', path: '/nora/library/master-data/genre', kind: 'tab' },
        ],
      },
    ],
  },

  // ── Library (library_user) ─────────────────────────────────────────────
  // libraryNavList exposes Digital/Physical Library destinations to readers.
  {
    label: 'Library', path: '/nora/library/physical-books/digital-library', roles: R_LIBRARY_USER,
    children: [
      { label: 'Digital Library', path: '/nora/library/physical-books/digital-library' },
      { label: 'Physical Library', path: '/nora/library/physical-books/physical-library' },
    ],
  },

  // ── Asset Management ───────────────────────────────────────────────────
  // Mirrors assetNavList in nav-menu.constants.ts.
  {
    label: 'Asset Management', path: '/nora/asset-management/dashboard', roles: R_ASSET,
    children: [
      { label: 'Dashboard', path: '/nora/asset-management/dashboard' },
      {
        label: 'Assets', path: '/nora/asset-management/assets',
        children: [
          { label: 'Manage Assets', path: '/nora/asset-management/assets' },
          { label: 'Asset Return', path: '/nora/asset-management/asset-returns' },
          { label: 'Asset History', path: '/nora/asset-management/asset-history' },
        ],
      },
      { label: 'Reports', path: '/nora/asset-management/reports' },
      {
        label: 'Settings', path: '/nora/asset-management/settings',
        children: [
          { label: 'Users', path: '/nora/asset-management/users' },
          { label: 'Categories', path: '/nora/asset-management/categories' },
          { label: 'Locations', path: '/nora/asset-management/locations' },
          { label: 'Brands', path: '/nora/asset-management/brands' },
          { label: 'Models', path: '/nora/asset-management/models' },
        ],
      },
    ],
  },

  // ── Application Tracker ────────────────────────────────────────────────
  // Mirrors applicationTrackerNavList. Inner tabs come from
  // application-tracker-applications.component.html (setActiveTab buttons).
  {
    label: 'Application Tracker', path: '/nora/application-tracker/applications', roles: R_APPTRACKER,
    children: [
      { label: 'Overview', path: '/nora/application-tracker/dashboard' },
      { label: 'Create Campaign', path: '/nora/application-tracker/campaigns' },
      {
        label: 'Applications', path: '/nora/application-tracker/applications',
        children: [
          { label: 'Applications Received', path: '/nora/application-tracker/applications?tab=applications', kind: 'subtab' },
          { label: 'Shortlisted', path: '/nora/application-tracker/applications?tab=shortlisted', kind: 'subtab' },
          { label: 'Interview Summary', path: '/nora/application-tracker/applications?tab=interviews', kind: 'subtab' },
          { label: 'Admission Confirmed', path: '/nora/application-tracker/applications?tab=confirmed', kind: 'subtab' },
          { label: 'Enrollment Finalized', path: '/nora/application-tracker/applications?tab=finalize', kind: 'subtab' },
        ],
      },
      {
        // Wired via application-tracker-rejected.component.ts queryParamMap subscription.
        label: 'Rejections', path: '/nora/application-tracker/rejected',
        children: [
          { label: 'Rejected Applications', path: '/nora/application-tracker/rejected?tab=rejected', kind: 'tab' },
          { label: 'Rejected Interviews', path: '/nora/application-tracker/rejected?tab=failed', kind: 'tab' },
        ],
      },
      { label: 'Fee Collection', path: '/nora/application-tracker/fee-collection' },
      { label: 'Settings', path: '/nora/application-tracker/settings' },
    ],
  },

  // ── Store Management ───────────────────────────────────────────────────
  // Mirrors storeNavList in nav-menu.constants.ts.
  {
    label: 'Store', path: '/nora/store-management/dashboard', roles: R_STORE,
    children: [
      { label: 'Dashboard', path: '/nora/store-management/dashboard' },
      {
        label: 'Product Management', path: '/nora/store-management/assets',
        children: [
          { label: 'Attributes', path: '/nora/store-management/attribute' },
          { label: 'Category', path: '/nora/store-management/category' },
          { label: 'Products', path: '/nora/store-management/products' },
        ],
      },
      { label: 'Inventory', path: '/nora/store-management/inventory' },
      { label: 'Orders', path: '/nora/store-management/orders' },
      { label: 'Vendors', path: '/nora/store-management/vendors' },
      { label: 'Purchases', path: '/nora/store-management/purchases' },
      { label: 'Accounts', path: '/nora/store-management/accounts' },
      { label: 'Banner Ads', path: '/nora/store-management/banner-ads' },
      { label: 'Reports', path: '/nora/store-management/reports' },
      {
        // Wired via settings.component.ts queryParamMap subscription.
        label: 'Settings', path: '/nora/store-management/settings',
        children: [
          { label: 'Payment Gateway', path: '/nora/store-management/settings?tab=payment', kind: 'tab' },
          { label: 'Tax / GST', path: '/nora/store-management/settings?tab=tax', kind: 'tab' },
          { label: 'Order Fulfillment', path: '/nora/store-management/settings?tab=fulfillment', kind: 'tab' },
          { label: 'Notifications', path: '/nora/store-management/settings?tab=notifications', kind: 'tab' },
          { label: 'User Roles', path: '/nora/store-management/settings?tab=roles', kind: 'tab' },
        ],
      },
    ],
  },

  // ── MIS ────────────────────────────────────────────────────────────────
  // Mirrors misNavList in nav-menu.constants.ts.
  {
    label: 'MIS', path: '/nora/mis/dashboard', roles: R_MIS,
    children: [
      { label: 'Overview', path: '/nora/mis/dashboard' },
      { label: 'Academics', path: '/nora/mis/academics' },
      { label: 'Finance', path: '/nora/mis/finance' },
      { label: 'HR', path: '/nora/mis/hr' },
      { label: 'Performance', path: '/nora/mis/performance' },
      { label: 'Usage', path: '/nora/mis/usage' },
      {
        // Wired via mis-config.component.ts queryParamMap subscription.
        label: 'Configuration', path: '/nora/mis/config',
        children: [
          { label: 'Budget Management', path: '/nora/mis/config?tab=budget', kind: 'tab' },
          { label: 'KPI Targets', path: '/nora/mis/config?tab=targets', kind: 'tab' },
        ],
      },
    ],
  },

  // ── Visitor Management — currently HIDDEN in user shell nav ────────────
  // Module commented out in user.component.ts navlist. Skip from search.

  // ── Parent — currently HIDDEN in user shell nav (separate parent shell) ─
  // parentNavList is only used inside the parent shell, not the staff shell.
  // Skip from staff search until/unless parent search is in scope.
];
